import { useEffect } from 'react'
import { useConfigStore } from '@/stores/useConfigStore'
import { configService } from '@/services/configService'
import { IAccount } from '@/types'
import useUIStore from '@/stores/useUIStore'
import { useTaskSortStore } from '@/stores/useTaskSortStore'

export const useConfig = () => {
  const config = useConfigStore((state) => state.config)
  const setLayout = useConfigStore((state) => state.setLayout)
  const setListOrder = useConfigStore((state) => state.setListOrder)
  const setAccountNickname = useConfigStore((state) => state.setAccountNickname)
  const setTasklistSortType = useConfigStore((state) => state.setTasklistSortType)
  const cleanupAccount = useConfigStore((state) => state.cleanupAccount)
  const cleanupTasklist = useConfigStore((state) => state.cleanupTasklist)

  return {
    config,
    setLayout,
    setListOrder,
    setAccountNickname,
    setTasklistSortType,
    cleanupAccount,
    cleanupTasklist
  }
}

export const useConfigSync = (accounts: Record<string, IAccount> | undefined) => {
  const setConfig = useConfigStore((state) => state.setConfig)
  const isLoaded = useConfigStore((state) => state.isLoaded)

  useEffect(() => {
    if (!accounts || isLoaded) return

    const loadConfig = async () => {
      try {
        const localConfig = configService.getFromLocalStorage()
        const firestoreConfig = await configService.getFromFirestore()
        
        let mergedConfig = configService.mergeConfigs(localConfig, firestoreConfig)
        
        const accountIds = Object.keys(accounts)
        mergedConfig = configService.validateConfig(mergedConfig, accountIds)
        
        setConfig(mergedConfig)
        configService.saveToLocalStorage(mergedConfig)
        
        useUIStore.setState({ layout: mergedConfig.layout })
        
        Object.keys(mergedConfig.accounts).forEach(accountId => {
          if (mergedConfig.accounts[accountId]?.listOrder) {
            useUIStore.setState((state) => ({
              listOrder: {
                ...state.listOrder,
                [accountId]: mergedConfig.accounts[accountId].listOrder
              }
            }))
          }
        })
        
        Object.keys(mergedConfig.tasklists).forEach(accountId => {
          Object.keys(mergedConfig.tasklists[accountId]).forEach(tasklistId => {
            const sortType = mergedConfig.tasklists[accountId][tasklistId]?.sortType
            if (sortType) {
              useTaskSortStore.setState((state) => ({
                tasklistSortTypes: {
                  ...state.tasklistSortTypes,
                  [accountId]: {
                    ...state.tasklistSortTypes[accountId],
                    [tasklistId]: sortType
                  }
                }
              }))
            }
          })
        })
      } catch (error) {
        console.warn('Error loading config:', error)
        const localConfig = configService.getFromLocalStorage()
        if (localConfig) {
          const accountIds = Object.keys(accounts)
          const validatedConfig = configService.validateConfig(localConfig, accountIds)
          setConfig(validatedConfig)
          
          useUIStore.setState({ layout: validatedConfig.layout })
          Object.keys(validatedConfig.accounts).forEach(accountId => {
            if (validatedConfig.accounts[accountId]?.listOrder) {
              useUIStore.setState((state) => ({
                listOrder: {
                  ...state.listOrder,
                  [accountId]: validatedConfig.accounts[accountId].listOrder
                }
              }))
            }
          })
        }
      }
    }

    loadConfig()
  }, [accounts, isLoaded, setConfig])
}

export const useConfigCleanup = () => {
  const { cleanupAccount, cleanupTasklist } = useConfig()
  
  return {
    cleanupAccount,
    cleanupTasklist
  }
}

